// src/pages/public/Login.jsx
import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { loginUser } from "../../api/api";
import { useNavigate, Link } from "react-router-dom";
import roscoImg from "../../assets/images/RoscoTransparente.gif";
import faecaAgro360Img from "../../assets/images/FaecaAGRO360Transparente.png";
import { useBiometricAuth } from "../../hooks/useBiometricAuth";

/**
 * Componente de página de inicio de sesión.
 * Muestra un formulario de login con animaciones de logos y maneja la autenticación del usuario.
 * Redirige según el rol del usuario tras un login exitoso.
 * 
 * @returns {JSX.Element} Página de login con animaciones
 */
export default function Login() {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [disabledModal, setDisabledModal] = useState(false);
  const [showAppInfoModal, setShowAppInfoModal] = useState(false);
  const [animationStep, setAnimationStep] = useState(1); // 1: rosco creciendo, 2: faeca aparece, 3: rosco se mueve
  const navigate = useNavigate();
  const { isSupported: biometricSupported, authenticate: biometricAuth, register: biometricRegister, loading: biometricLoading, hasBiometricForEmail, saveBiometricEmail } = useBiometricAuth();
  const [showBiometricBtn, setShowBiometricBtn] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null); // datos del login normal, pendiente de ofrecer biometría

  // Comprobar si hay email con biometría guardado
  useEffect(() => {
    const savedEmail = localStorage.getItem("biometric_email");
    if (savedEmail && biometricSupported) {
      setEmail(savedEmail);
      setShowBiometricBtn(true);
    }
  }, [biometricSupported]);

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setAnimationStep(2); 
    }, 1500);

    const timer2 = setTimeout(() => {
      setAnimationStep(3); 
    }, 2200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = await loginUser(email, password);

      // Si soporta biometría y no la tiene activada, ofrecer activarla
      if (biometricSupported && !hasBiometricForEmail(email)) {
        setPendingLoginData(data);
        setShowBiometricSetup(true);
        return;
      }

      login(data.user, data.token, data.refreshToken);
      navigate("/");
    } catch (err) {
      const msg = err.response?.data?.message || "Error al iniciar sesión";
      setMensaje(msg);
      if (err.response?.status === 403 && /deshabilad/i.test(msg)) {
        setDisabledModal(true);
      }
    }
  };

  // Completar login después de decidir sobre biometría
  const finishLogin = (data) => {
    login(data.user, data.token, data.refreshToken);
    setShowBiometricSetup(false);
    setPendingLoginData(null);
    navigate("/");
  };

  // Activar biometría y luego completar login
  const handleActivateBiometric = async () => {
    if (!pendingLoginData) return;
    const result = await biometricRegister(pendingLoginData.token);
    if (result.success) {
      saveBiometricEmail(email);
    }
    finishLogin(pendingLoginData);
  };

  // Saltar biometría y completar login
  const handleSkipBiometric = () => {
    if (pendingLoginData) finishLogin(pendingLoginData);
  };

  const handleBiometricLogin = async () => {
    const emailToUse = email || localStorage.getItem("biometric_email");
    if (!emailToUse) {
      setMensaje("Introduce tu email para usar acceso biométrico");
      return;
    }
    setMensaje("");
    const result = await biometricAuth(emailToUse);
    if (result.success) {
      login(result.data.user, result.data.token, result.data.refreshToken);
      navigate("/");
    } else {
      setMensaje(result.message);
    }
  };

  return (
    <>
      <style>{`
        @keyframes scaleIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
      
      <div className="min-h-screen flex bg-gray-50 overflow-hidden relative">
      {/* Rosco de fondo - centrado respecto al formulario (lado derecho) */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-1/2 flex items-center justify-center pointer-events-none z-0">
        <img 
          src={roscoImg} 
          alt="Rosco FAECA Background" 
          className="w-[600px] h-[600px] object-contain opacity-35"
        />
      </div>

      {/* Lado izquierdo - Animaciones */}
      <div className="hidden lg:flex lg:w-1/2 bg-white items-center justify-center relative overflow-hidden z-10">
        <div className="flex items-center justify-center gap-4">
          {/* Rosco - aparece de pequeño a grande, luego se mueve a la izquierda cuando faeca se coloca */}
          <div 
            className={`transition-all duration-1000 ease-in-out ${
              animationStep === 1 
                ? 'scale-0 opacity-0' 
                : animationStep === 3 
                ? '-translate-x-8 opacity-100' 
                : 'scale-100 opacity-100'
            }`}
            style={{
              animation: animationStep === 1 ? 'scaleIn 1.5s ease-out forwards' : 'none'
            }}
          >
            <img 
              src={roscoImg} 
              alt="Rosco FAECA" 
              className="w-48 h-48 object-contain"
            />
          </div>

          {/* FAECA AGRO360 - se desliza desde la izquierda hacia la derecha como un bloque */}
          <div 
            className={`transition-all duration-1000 ease-in-out ${
              animationStep >= 2 
                ? 'translate-x-0 opacity-100' 
                : '-translate-x-full opacity-0'
            }`}
          >
            <img 
              src={faecaAgro360Img} 
              alt="FAECA AGRO360" 
              className="w-[400px] h-[400px] object-contain"
            />
          </div>
        </div>
      </div>

      {/* Lado derecho - Formulario */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 md:p-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Header minimalista */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              Iniciar Sesión
            </h1>
            <p className="text-gray-500">
              Accede a tu panel de control
            </p>
          </div>

          {/* Mensajes de error */}
          {mensaje && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded mb-6">
              <div className="flex items-center gap-2">
                <i className="fas fa-exclamation-circle"></i>
                <span className="text-sm">{mensaje}</span>
              </div>
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo electrónico
              </label>
              <input
                type="email"
                placeholder="correo@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition"
                required
              />
            </div>

            <div className="mb-6 text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              className="w-full bg-gray-800 text-white py-3 rounded-lg hover:bg-gray-900 transition font-medium shadow-sm"
            >
              Iniciar Sesión
            </button>

            {/* Botón de acceso biométrico */}
            {biometricSupported && showBiometricBtn && (
              <button
                type="button"
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition font-medium disabled:opacity-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-6 2c0 3.31 2.69 6 6 6s6-2.69 6-6h-2c0 2.21-1.79 4-4 4s-4-1.79-4-4H6zm6-10C6.48 3 2 7.48 2 13h2c0-4.42 3.58-8 8-8s8 3.58 8 8h2c0-5.52-4.48-10-10-10zm0 4c-3.31 0-6 2.69-6 6h2c0-2.21 1.79-4 4-4s4 1.79 4 4h2c0-3.31-2.69-6-6-6z" />
                </svg>
                {biometricLoading ? "Verificando..." : "Acceder con huella / Face ID"}
              </button>
            )}
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Al iniciar sesión, aceptas nuestra{' '}
              <a href="/politica-privacidad" className="text-gray-700 hover:text-gray-900 underline">
                Política de Privacidad
              </a>
            </p>
            <div className="mt-4 text-center">
              <span className="text-xs text-gray-400">Desarrollado por </span>
              <a 
                href="https://www.faecagranada.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-gray-700 hover:text-gray-900 font-medium"
              >
                FAECA Granada
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Botón info móvil */}
      <button
        onClick={() => setShowAppInfoModal(true)}
        className="lg:hidden fixed top-4 right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:bg-gray-50 transition z-10 border border-gray-200"
        title="¿Cómo agregar acceso directo?"
      >
        <i className="fas fa-info"></i>
      </button>

      {/* Modal usuario deshabilitado */}
      {disabledModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <i className="fas fa-ban text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Acceso denegado</h3>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <p className="text-gray-700 text-sm">
                  Tu usuario o la empresa asociada se encuentra <strong>deshabilitada</strong>. 
                  Contacta con un administrador para más información.
                </p>
              </div>
              
              <div className="flex justify-end">
                <button 
                  onClick={() => setDisabledModal(false)} 
                  className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-900 transition font-medium"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de información de acceso directo */}
      {showAppInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-mobile-alt text-2xl text-gray-700"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">Acceso Directo</h3>
                </div>
                <button
                  onClick={() => setShowAppInfoModal(false)}
                  className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition"
                >
                  <i className="fas fa-times text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Contenido */}
            <div className="p-6 space-y-6">
              <p className="text-gray-600 text-sm">
                Puedes agregar esta página web como un acceso directo en tu dispositivo móvil o tablet para acceder rápidamente.
              </p>

              {/* iOS/Safari */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className="fab fa-apple text-2xl text-gray-700"></i>
                  <h4 className="font-bold text-gray-800">iOS / Safari</h4>
                </div>
                <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                  <li>Abre esta página en <strong>Safari</strong></li>
                  <li>Pulsa el botón de <strong>Compartir</strong> <i className="fas fa-share text-gray-600"></i></li>
                  <li>Selecciona <strong>"Añadir a pantalla de inicio"</strong></li>
                  <li>Confirma pulsando <strong>"Añadir"</strong></li>
                </ol>
              </div>

              {/* Android/Chrome */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className="fab fa-android text-2xl text-gray-700"></i>
                  <h4 className="font-bold text-gray-800">Android / Chrome</h4>
                </div>
                <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                  <li>Abre esta página en <strong>Chrome</strong></li>
                  <li>Pulsa el menú <i className="fas fa-ellipsis-v text-gray-600"></i> (tres puntos)</li>
                  <li>Selecciona <strong>"Añadir a pantalla de inicio"</strong> o <strong>"Instalar aplicación"</strong></li>
                  <li>Confirma pulsando <strong>"Añadir"</strong></li>
                </ol>
              </div>

              {/* Samsung Internet */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className="fas fa-mobile-alt text-2xl text-gray-700"></i>
                  <h4 className="font-bold text-gray-800">Samsung Internet</h4>
                </div>
                <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                  <li>Abre esta página en <strong>Samsung Internet</strong></li>
                  <li>Pulsa el menú <i className="fas fa-bars text-gray-600"></i></li>
                  <li>Selecciona <strong>"Añadir página a"</strong></li>
                  <li>Elige <strong>"Pantalla de inicio"</strong></li>
                </ol>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <i className="fas fa-lightbulb text-gray-600 text-xl mt-1"></i>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-1">Consejo</h5>
                    <p className="text-sm text-gray-600">
                      Una vez agregado, el acceso directo aparecerá como un icono en tu pantalla de inicio, 
                      permitiéndote abrir la aplicación con un solo toque.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de activar acceso biométrico (tras login exitoso) */}
      {showBiometricSetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-6 2c0 3.31 2.69 6 6 6s6-2.69 6-6h-2c0 2.21-1.79 4-4 4s-4-1.79-4-4H6zm6-10C6.48 3 2 7.48 2 13h2c0-4.42 3.58-8 8-8s8 3.58 8 8h2c0-5.52-4.48-10-10-10zm0 4c-3.31 0-6 2.69-6 6h2c0-2.21 1.79-4 4-4s4 1.79 4 4h2c0-3.31-2.69-6-6-6z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Acceso rápido</h3>
                  <p className="text-sm text-gray-500">Huella dactilar / Face ID</p>
                </div>
              </div>

              <p className="text-gray-600 mb-2 text-sm leading-relaxed">
                ¿Quieres activar el acceso biométrico para iniciar sesión más rápido la próxima vez?
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded mb-6">
                <p className="text-xs text-blue-700">
                  Tu huella o rostro nunca sale del dispositivo. Es seguro y solo funciona en este teléfono.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSkipBiometric}
                  className="flex-1 bg-gray-100 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Ahora no
                </button>
                <button
                  onClick={handleActivateBiometric}
                  disabled={biometricLoading}
                  className="flex-1 bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {biometricLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Activando...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-6 2c0 3.31 2.69 6 6 6s6-2.69 6-6h-2c0 2.21-1.79 4-4 4s-4-1.79-4-4H6zm6-10C6.48 3 2 7.48 2 13h2c0-4.42 3.58-8 8-8s8 3.58 8 8h2c0-5.52-4.48-10-10-10zm0 4c-3.31 0-6 2.69-6 6h2c0-2.21 1.79-4 4-4s4 1.79 4 4h2c0-3.31-2.69-6-6-6z" />
                      </svg>
                      Activar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
